import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { customersActionCreators } from '../../store/customers';
import { Contact } from '../../interfaces/Contact';
import { ContactsActionCreators } from '../../store/contacts';
import { TeamsState } from '../../store/TeamsState';
import { AddIcon, ArrowClockwiseIcon, EditIcon, FilterIcon, useEditorStyles, useTabStyles } from '../Styles/Editor';
import { Button, InputOnChangeData, Popover, PopoverSurface, PopoverTrigger, SearchBox, SearchBoxChangeEvent, Skeleton, SkeletonItem, Table, TableBody, TableCell, TableCellActions, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Tag } from '@fluentui/react-components';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { useCustomers } from '../Tools/ObjectLoader';

export const ContactsTab = () => {
    const { customers } = useCustomers();
    const contacts = useSelector<ApplicationState, Contact[] | undefined>((state: ApplicationState) => state.contactsState.contacts);
    const contactsLoading = useSelector((state: ApplicationState) => state.contactsState.contactsLoading);
    const [filter, setFilter] = React.useState<string | null>(null);
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { t } = useTranslation();
    const [filterCustomer, setFilterCustomer] = React.useState<Customer | undefined>(undefined);
    const [filteredContact, setFilteredContact] = React.useState<Contact[] | undefined>(undefined);
    const styles = useEditorStyles();
    const stylesTab = useTabStyles();

    // Check if all needed datas are retrieven
    React.useEffect(() => {
        if (contacts === undefined && contactsLoading === false) {
            dispatch(ContactsActionCreators.requestContacts(instance, teamsState));
        }
    }, [instance, teamsState, contacts, contactsLoading]);

    React.useEffect(() => {
        if (contacts !== undefined) {
            contacts.forEach(c => {
                if (c.contactCustomerRole === null && c.contactCustomerRoleLoading === false && c.contactid) {
                    dispatch(ContactsActionCreators.requestContactsRoles(instance, teamsState, c.contactid));
                }
            });
        }
    }, [contacts]);

    React.useEffect(() => {
        if (customers != undefined && contacts != undefined) {
            let filtered = contacts;
            // Apply filter if any
            if (filterCustomer) {
                filtered = filtered
                    .filter(ts => ts.contactCustomerRole && ts.contactCustomerRole.find(r => r.customerid === filterCustomer.customerid) !== undefined);
            }
            if (filter && filter.length != 0) {
                filtered = filtered.filter(t => (t.firstName && t.firstName.toLowerCase().includes(filter.toLowerCase()))
                    || (t.lastName && t.lastName.toLowerCase().includes(filter.toLowerCase())));
            }
            setFilteredContact(filtered);
        }
    }, [customers, contacts, filter, filterCustomer]);

    const columns = [
        { columnKey: "Name", label: t('Name') },
        { columnKey: "Email", label: "Email" },
        { columnKey: "Customers", label: t("Customer") },
    ];

    return <div className={stylesTab.rootSub}>
        <div className={stylesTab.MainBar}>
            <Popover positioning="below-start">
                <PopoverTrigger>
                    <Button icon={<FilterIcon />} shape="circular" title="Filter" />
                </PopoverTrigger>
                <PopoverSurface>
                    <div className={stylesTab.List}>
                        {t('Customer')}
                        <CustomerDropdown currentCustomer={filterCustomer ?? undefined}
                            customers={customers ?? []}
                            setCustomer={setFilterCustomer} />
                    </div>
                </PopoverSurface>
            </Popover>
            {filterCustomer &&
                <Tag
                    dismissible
                    dismissIcon={{ "aria-label": "remove" }}
                    size="small"
                    onClick={() => { setFilterCustomer(undefined); }}
                    appearance="outline"
                >
                    {t('Customer') + " = " + filterCustomer.displayName}
                </Tag>
            }
            <SearchBox
                placeholder={t('Contains')}
                onChange={(e: SearchBoxChangeEvent, data: InputOnChangeData) => { setFilter(data.value); }}
                dismiss={{ onClick: () => { setFilter(null); } }}
                className={styles.fluid}
            />
            <div className={stylesTab.MarginLeftAuto}>
                <Button icon={<AddIcon />} shape="circular" title={t('Add')}
                    onClick={() => {
                        const c: Contact = {
                            contactid: null,
                            title: null,
                            firstName: null,
                            lastName: null,
                            email: null,
                            phone: null,
                            mobile: null,
                            description: null,
                            defaultLanguage: null,
                            linkedIn: null,
                            contactCustomerRoleLoading: false,
                            contactCustomerRole: null,
                        };
                        dispatch(ContactsActionCreators.requestContactEdition(c));
                    }} />
                <Button icon={<ArrowClockwiseIcon />} shape="circular" title="Reload"
                    onClick={() => dispatch(ContactsActionCreators.ReloadContactsEdition())}
                />
            </div>
        </div>
        <Table arial-label="Default table">
            <TableHeader>
                <TableRow>
                    {columns.map((column) => (
                        <TableHeaderCell key={column.columnKey}>
                            {column.label}
                        </TableHeaderCell>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {filteredContact === undefined
                    ? [...Array(10)].map((_, index) =>
                        <TableRow key={index}>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                        </TableRow>)
                    : filteredContact.map(c => {
                        const custList: JSX.Element[] = [];
                        if (c.contactCustomerRole && customers) {
                            c.contactCustomerRole.forEach((r) => {
                                const cust = customers.find(x => x.customerid === r.customerid);
                                if (cust && cust.displayName) {
                                    custList.push(
                                        <Button appearance="transparent"
                                            className={stylesTab.TestButton}
                                            onClick={() => { dispatch(customersActionCreators.requestCustomerQuickView(cust)); }} >
                                            {cust.displayName + (r.relationShipTypeId ? " (" + t(r.relationShipTypeId) + ")" : "")}
                                        </Button>
                                    );
                                }
                            });
                        }
                        return <TableRow key={c.contactid}>
                            <TableCell>
                                <TableCellLayout>
                                    <Button appearance="transparent" className={stylesTab.TestButton} onClick={() => dispatch(ContactsActionCreators.requestContactQuickView(c))}>{c.firstName + ' ' + c.lastName}</Button>
                                </TableCellLayout>
                                <TableCellActions>
                                    <Button
                                        icon={<EditIcon />}
                                        appearance="subtle"
                                        aria-label={t('Edit')}
                                        onClick={() => dispatch(ContactsActionCreators.requestContactEdition(c))}
                                    />
                                </TableCellActions>
                            </TableCell>
                            <TableCell>{c.email}</TableCell>
                            <TableCell>
                                {c.contactCustomerRole
                                    ? <div className={stylesTab.List}>{custList.map(i => i)}</div>
                                    : <Skeleton animation="pulse">
                                        <SkeletonItem className={stylesTab.width15} />
                                    </Skeleton>
                                }
                            </TableCell>
                        </TableRow>;
                    }
                    )}
            </TableBody>
        </Table>
    </div>;
};